import React, { useReducer, useState } from "react";
import CarrierDriver from "./CarrierDriver";
import ConfigureLoad from "./ConfigureLoad";
import OrderNumber from "./OrderNumber";
import SelectDestination from "./SelectDestination";
import { CarrierDriverInfo, StagingSettings } from "../../requests/useGetStagingSettings";
import { Destination, LoadoutGroup, Order, PurchaseOrder, SODestination } from "../../UserFlow";
import useCreateStagedOrder, { AnsweredQuestion, Compartment, CreateStagedOrder } from "../../requests/useCreateStagedOrder";
import LoadQuestion from "./LoadQuestions";
import { FooterButtonProps } from "../../common/FooterButton";
import useDisplayMessage from "../../common/lib/hooks/useDisplayMessage";
import { StagedOrder, StagedPurchaseOrder } from "./StageOrderTypes";
import { useLingui } from "@lingui/react";
import { msg } from "@lingui/macro";
import { driverFacingErrorMessage } from "../../common/userFacingMessages/userFacingMessages";
import { PurchaseOrderInfo } from "./PurchaseOrderInfo";
import useCreateStagedPurchaseOrder, { CreateStagedPurchaseOrder } from "../../requests/useCreateStagedPurchaseOrder";
import { UserFacingMessageCode } from "../../common/userFacingMessages/userFacingMessageCodes";
import { PopUpMessageView } from "../../common/PopUpMessageView";

enum Step {
  CarrierDriver,
  EnterNumber,
  SelectDestination,
  ConfigureLoad,
  LoadQuestions,
  PurchaseOrderInfo,
  Done,
}

enum ActionType {
  Back,
  CarrierDriverEntered,
  OrderNumberEntered,
  PurchaseOrderNumberEntered,
  LoadoutGroupNameEntered,
  DestinationSelected,
  CompartmentsConfigured,
  StagedOrderCreated,
  LoadQuestionsAnswered,
  StagedPurchaseOrderCreated,
}

type WithKioskId = { kioskId?: string };
type WithSettings = { settings: StagingSettings };
type WithBase = WithSettings & WithKioskId;
type WithOrderOrLoadoutGroupDetails = WithSettings & WithKioskId & {
  carrierDriverInfo: CarrierDriverInfo,
  order?: Order,
  loadoutGroup?: LoadoutGroup,
  destination?: SODestination,
  compartments: Compartment[]
};
type WithPurchaseOrderDetails = WithSettings & WithKioskId & {
  carrierDriverInfo: CarrierDriverInfo,
  purchaseOrder: PurchaseOrder,
};

type State =
  | { step: Step.CarrierDriver } & WithBase
  | { step: Step.EnterNumber, initialNumber?: string } & Omit<WithOrderOrLoadoutGroupDetails, 'compartments' | 'destination'>
  | { step: Step.SelectDestination, initialDestination?: SODestination } & Omit<WithOrderOrLoadoutGroupDetails, 'compartments' | 'destination'>
  | { step: Step.ConfigureLoad, initialCompartments?: Compartment[] } & Omit<WithOrderOrLoadoutGroupDetails, 'compartments'>
  | { step: Step.LoadQuestions } & WithOrderOrLoadoutGroupDetails
  | { step: Step.PurchaseOrderInfo } & WithPurchaseOrderDetails

type Action =
  | { type: ActionType.Back }
  | { type: ActionType.CarrierDriverEntered, carrierDriverInfo: CarrierDriverInfo }
  | { type: ActionType.OrderNumberEntered, order: Order }
  | { type: ActionType.LoadoutGroupNameEntered, loadoutGroup: LoadoutGroup }
  | { type: ActionType.DestinationSelected, destination?: SODestination }
  | { type: ActionType.CompartmentsConfigured, compartments: Compartment[], create: (createSo: CreateStagedOrder) => void }
  | { type: ActionType.LoadQuestionsAnswered, answeredQuestions: AnsweredQuestion[], create: (createSo: CreateStagedOrder) => void }
  | { type: ActionType.PurchaseOrderNumberEntered, purchaseOrder: PurchaseOrder }
  | { type: ActionType.StagedPurchaseOrderCreated, create: (createSpo: CreateStagedPurchaseOrder) => void }

function reducer(state: State, action: Action): State {
  const showDestinations = state.settings.showCustomerDestination;

  switch (action.type) {
    case ActionType.Back:
      if (state.step === Step.SelectDestination || (state.step === Step.ConfigureLoad && !showDestinations)) {
        return { ...state, step: Step.EnterNumber, initialNumber: state.order?.number ?? state.loadoutGroup!.name, order: undefined, loadoutGroup: undefined };
      } else if (state.step === Step.ConfigureLoad && showDestinations) {
        return { ...state, step: Step.SelectDestination, initialDestination: state.destination };
      } else if (state.step === Step.LoadQuestions) {
        return { ...state, step: Step.ConfigureLoad, initialCompartments: state.compartments }
      } else if (state.step === Step.PurchaseOrderInfo) {
        return { ...state, step: Step.EnterNumber, initialNumber: state.purchaseOrder.number }
      } else if (state.step === Step.EnterNumber && state.settings.requireAuthentication)
        return { ...state, step: Step.CarrierDriver }
      break;

    case ActionType.CarrierDriverEntered:
      if (state.step === Step.CarrierDriver) {
        return { ...state, step: Step.EnterNumber, carrierDriverInfo: action.carrierDriverInfo };
      }
      break;

    case ActionType.OrderNumberEntered:
      if (state.step === Step.EnterNumber && showDestinations) {
        return { ...state, ...action, initialDestination: action.order.destination, step: Step.SelectDestination };
      } else if (state.step === Step.EnterNumber && !showDestinations) {
        return { ...state, ...action, destination: action.order.destination, step: Step.ConfigureLoad };
      }
      break;

      
    case ActionType.LoadoutGroupNameEntered:
      if (state.step === Step.EnterNumber && showDestinations) {
        return { ...state, ...action, step: Step.SelectDestination };
      } else if (state.step === Step.EnterNumber && !showDestinations) {
        return { ...state, ...action, step: Step.ConfigureLoad };
      }
      break;

    case ActionType.DestinationSelected:
      if (state.step === Step.SelectDestination) {
        return { ...state, destination: action.destination, step: Step.ConfigureLoad };
      }
      break;

    case ActionType.CompartmentsConfigured:
      if (state.step !== Step.ConfigureLoad) break;
      if ((state.order && state.order.loadQuestions.length === 0) || (state.loadoutGroup && state.loadoutGroup.loadQuestions.length === 0)) {
        action.create({
          ...state.carrierDriverInfo,
          orderNumber: state.order?.number ?? state.loadoutGroup!.name,
          destinationId: getDestinationId(state.destination),
          shipToName: getShipToName(state.destination),
          compartments: action.compartments,
          answeredQuestions: [],
          kioskId: state.kioskId
        });

        return state;
      } else {
        return { ...state, ...action, step: Step.LoadQuestions };
      }

    case ActionType.LoadQuestionsAnswered:
      if (state.step !== Step.LoadQuestions) break;
      action.create({
        ...state.carrierDriverInfo,
        orderNumber: state.order?.number ?? state.loadoutGroup!.name,
        destinationId: getDestinationId(state.destination),
        shipToName: getShipToName(state.destination),
        compartments: state.compartments,
        answeredQuestions: action.answeredQuestions,
        kioskId: state.kioskId
      });
      return state;

    case ActionType.PurchaseOrderNumberEntered:
      if (state.step !== Step.EnterNumber) break;
      return { ...state, step: Step.PurchaseOrderInfo, ...action };

    case ActionType.StagedPurchaseOrderCreated:
      if (state.step !== Step.PurchaseOrderInfo) break;
      action.create({
        ...state.carrierDriverInfo,
        purchaseOrderNumber: state.purchaseOrder.number,
        kioskId: state.kioskId
      });
      return state;
  }

  throw Error(`Invalid action received: ${JSON.stringify(action)} for state: ${JSON.stringify(state)}`);
}

const initializer = ({ settings, kioskId }: Props): State => {
  const common = {
    totalSteps: (settings.showCustomerDestination ? 3 : 2),
    settings,
  }

  if (settings.requireAuthentication) {
    return { ...common, step: Step.CarrierDriver, kioskId: kioskId };
  } else {
    return {
      ...common,
      step: Step.EnterNumber,
      carrierDriverInfo: {
        carrierNumber: '',
        driverNumber: '',
        driverPassword: '',
        transportNumber: '',
      },
      kioskId: kioskId
    };
  }
}

const isDestination = (dest?: SODestination): dest is Destination => !!dest && ("id" in dest);

const getShipToName = (destination?: SODestination) =>
  isDestination(destination) ? undefined : destination?.name;

const getDestinationId = (destination?: SODestination) =>
  !isDestination(destination) ? undefined : destination.id;

type Props = {
  siteId: string,
  kioskId?: string,
  enableRememberMe: boolean,
  settings: StagingSettings;
  stagedOrderCreated: (so: StagedOrder) => void;
  stagedPurchaseOrderCreated: (spo: StagedPurchaseOrder) => void;
  backToStart: () => void;
}

const StageOrderFlow: React.FC<Props> = (props) => {
  const { _ } = useLingui();
  const [state, dispatch] = useReducer(reducer, props, initializer);
  const createDisplay = useDisplayMessage();
  const [alertMessage, setAlertMessage] = useState<string | undefined>(undefined);

  const createSoRequest = useCreateStagedOrder(props.siteId, {
    onSuccess: so => props.stagedOrderCreated(so),
    onError: err => {
      if (err.errorCode === UserFacingMessageCode.IncorrectAnswersSuspendsDriver ||
            err.errorCode === UserFacingMessageCode.IncorrectAnswers) {
        setAlertMessage(_(driverFacingErrorMessage(err)));
        return;
      }

      createDisplay.fail(_(driverFacingErrorMessage(err)));
    },
  });

  const createSpoRequest = useCreateStagedPurchaseOrder(props.siteId, {
    onSuccess: spo => props.stagedPurchaseOrderCreated(spo),
    onError: err => createDisplay.fail(_(driverFacingErrorMessage(err))),
  });

  const carrierDriverEntered = (carrierDriverInfo: CarrierDriverInfo) =>
    dispatch({ type: ActionType.CarrierDriverEntered, carrierDriverInfo });

  const orderSelected = (order: Order) =>
    dispatch({ type: ActionType.OrderNumberEntered, order });

  const purchaseOrderSelected = (purchaseOrder: PurchaseOrder) =>
    dispatch({ type: ActionType.PurchaseOrderNumberEntered, purchaseOrder });

  const loadoutGroupSelected = (loadoutGroup: LoadoutGroup) =>
    dispatch({ type: ActionType.LoadoutGroupNameEntered, loadoutGroup });

  const destinationSelected = (destination?: SODestination) =>
    dispatch({ type: ActionType.DestinationSelected, destination });

  const compartmentsConfigured = (compartments: Compartment[]) =>
    dispatch({ type: ActionType.CompartmentsConfigured, compartments, create: createSoRequest.request });

  const loadQuestionsAnswered = (answeredQuestions: AnsweredQuestion[]) =>
    dispatch({ type: ActionType.LoadQuestionsAnswered, answeredQuestions, create: createSoRequest.request });

  const purchaseOrderInfoSelected = () =>
    dispatch({ type: ActionType.StagedPurchaseOrderCreated, create: createSpoRequest.request });

  const backProps: FooterButtonProps = {
    children: _(msg`Back`),
    onClick: () => dispatch({ type: ActionType.Back })
  };

  const backToStartProps: FooterButtonProps = {
    children: _(msg`Back`),
    onClick: () => props.backToStart()
  };

  const dispensingTotalSteps = 5;


  const currentPage = () => {
    switch (state.step) {
      case Step.CarrierDriver:
        return <CarrierDriver
          {...props.settings}
          siteId={props.siteId}
          carrierDriverEntered={carrierDriverEntered}
          enableRememberMe={props.enableRememberMe}
          backButton={backToStartProps}
          startOver={props.backToStart} />

      case Step.EnterNumber:
        return <OrderNumber
          {...props.settings}
          siteId={props.siteId}
          kioskId={props.kioskId}
          initialOrderNumber={state.initialNumber}
          orderSelected={orderSelected}
          purchaseOrderSelected={purchaseOrderSelected}
          loadoutGroupSelected={loadoutGroupSelected}
          progress={{ totalSteps: dispensingTotalSteps, currentStep: 1 }}
          backButton={props.settings.requireAuthentication ? backProps : backToStartProps}
          startOver={props.backToStart} />

      case Step.SelectDestination:
        return <SelectDestination
          {...props.settings}
          order={state.order}
          loadoutGroup={state.loadoutGroup}
          initialDestination={state.initialDestination}
          destinationSelected={destinationSelected}
          backButton={backProps}
          progress={{ totalSteps: dispensingTotalSteps, currentStep: 2 }}
          startOver={props.backToStart} />

      case Step.ConfigureLoad:
        return <ConfigureLoad
          {...props.settings}
          orderNumber={state.order?.number ?? state.loadoutGroup!.name}
          availableToRequest={state.order?.availableToRequest ?? state.loadoutGroup!.availableToRequest}
          compartmentsConfigured={compartmentsConfigured}
          initialCompartments={state.initialCompartments}
          message={createDisplay.message}
          clearMessage={createDisplay.clear}
          backButton={backProps}
          nextButton={{
            children: (state.order?.loadQuestions.length ?? state.loadoutGroup!.loadQuestions.length) > 0 ? _(msg`Next`) : _(msg`Complete`),
            spinning: createSoRequest.isLoading,
          }}
          progress={{ totalSteps: dispensingTotalSteps, currentStep: 3 }}
          startOver={props.backToStart} />

      case Step.LoadQuestions:
        return <LoadQuestion
          {...props.settings}
          loadQuestions={state.order?.loadQuestions ?? state.loadoutGroup!.loadQuestions}
          loadQuestionsAnswered={loadQuestionsAnswered}
          message={createDisplay.message}
          clearMessage={createDisplay.clear}
          backButton={backProps}
          nextButton={{ spinning: createSoRequest.isLoading }}
          progress={{ totalSteps: dispensingTotalSteps, currentStep: 4 }}
          startOver={props.backToStart} />

      case Step.PurchaseOrderInfo:
        return <PurchaseOrderInfo
          purchaseOrder={state.purchaseOrder}
          purchaseOrderInfoSelected={purchaseOrderInfoSelected}
          message={createDisplay.message}
          clearMessage={createDisplay.clear}
          backButton={backProps}
          nextButton={{
            spinning: createSpoRequest.isLoading,
            children: _(msg`Complete`)
          }}
          startOver={props.backToStart} />
    }
  }

  return <PopUpMessageView
    message={alertMessage}
    messageAcknowledged={props.backToStart}>
    {currentPage()}
  </PopUpMessageView>
}

export default StageOrderFlow;
